import { IPost } from '_interfaces/post.interface';
import { IPostType } from '_interfaces/systemPost.types';
import { IPostStatus } from '_interfaces/types/post.types';
import { filteredPostCommentsAtom, postCommentsAtom } from 'atoms/postComments';
import { commentMatchesSearch, postMatchesSearch } from 'atoms/utils/searchUtils';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  limit,
  startAfter,
  getDocs,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import { deleteMediaEntriesByPost } from 'utils/services/fileCabinetService';
import { atom } from 'jotai';
import { atomFamily, atomWithObservable, atomWithStorage, loadable } from 'jotai/utils';
import { Observable } from 'rxjs';
import { auth, db, refreshFirebaseToken } from 'utils/firebase';
import { selectedCompanyIdAtom } from '../company';
import { selectedProjectAtom } from '../projects';
import { TActivityFeedMediaMapped, TActivityFeedMediaWithIndex, UploadStatusEnum } from 'redux-store/api/activity-feed';
import { createCrudAtoms } from '../utils/crudFactory';

export const setActivityFeedSearchAtom = atom<string>('');

export const POSTS_PAGE_SIZE = 20;

const hasMoreBase = atom(true);
export const activityFeedHasMoreAtom = atom(
  (get) => get(hasMoreBase),
  (_get, set, value: boolean) => set(hasMoreBase, value)
);

const loadingMoreBase = atom(false);
export const activityFeedLoadingMoreAtom = atom(
  (get) => get(loadingMoreBase),
  (_get, set, value: boolean) => set(loadingMoreBase, value)
);

const additionalPostsBase = atom<IPost[]>([]);
export const activityFeedAdditionalPostsAtom = atom(
  (get) => get(additionalPostsBase),
  (_get, set, value: IPost[] | ((prev: IPost[]) => IPost[])) => set(additionalPostsBase, value)
);

export interface IActivityFeedFilter {
  phasesIds: number[] | null;
  phaseItemsIds: number[] | null;
  posterProfileIds: number[] | null;
  fromDate: string | null;
  toDate: string | null;
  isPhotoIncluded: boolean | null;
  isFileIncluded: boolean | null;
}

export const defaultActivityFeedFilter: IActivityFeedFilter = {
  phasesIds: null,
  phaseItemsIds: null,
  posterProfileIds: null,
  fromDate: null,
  toDate: null,
  isPhotoIncluded: null,
  isFileIncluded: null,
};

export const activityFeedFilterAtom = atom<IActivityFeedFilter>(defaultActivityFeedFilter);

const postMatchesFilter = (post: IPost, filter: IActivityFeedFilter): boolean => {
  if (filter.phasesIds?.length) {
    const postPhases = post.phaseIds || [];
    if (!filter.phasesIds.some(id => postPhases.includes(id))) {
      return false;
    }
  }

  if (filter.phaseItemsIds?.length) {
    const postItems = post.phaseItemIds || [];
    if (!filter.phaseItemsIds.some(id => postItems.includes(id))) {
      return false;
    }
  }

  if (filter.posterProfileIds?.length) {
    const postUserId = parseInt(post.createdBy?.userId || '0', 10);
    if (!filter.posterProfileIds.includes(postUserId)) {
      return false;
    }
  }

  if (filter.fromDate) {
    const postDate = new Date(post.createdAt);
    const fromDate = new Date(filter.fromDate);
    if (postDate < fromDate) {
      return false;
    }
  }

  if (filter.toDate) {
    const postDate = new Date(post.createdAt);
    const toDate = new Date(filter.toDate);
    if (postDate > toDate) {
      return false;
    }
  }

  if (filter.isPhotoIncluded === true) {
    const hasPhoto = post.media?.some(m => m.type === 'image');
    if (!hasPhoto) return false;
  }

  if (filter.isFileIncluded === true) {
    const hasFile = post.media?.some(m => m.type === 'file');
    if (!hasFile) return false;
  }

  return true;
};


export const activityFeedPostsCacheAtom = atomWithStorage<IPost[]>('activityFeedPostsCache', []);
export const localActivityFeedDraftPostsAtom = atomWithStorage<Partial<IPost>[]>(
  'localActivityFeedDraftPosts',
  []
);

export const activityFeedPostsAtom = atomWithObservable<IPost[]>(
  (get) => {
    const project = get(selectedProjectAtom);
    const companyId = get(selectedCompanyIdAtom);
    const searchQuery = get(setActivityFeedSearchAtom);

    const cached = get(activityFeedPostsCacheAtom);
    const initial: IPost[] = cached instanceof Promise ? [] : cached;

    if (!project?.id) {
      return new Observable<IPost[]>((subscriber) => {
        subscriber.next(initial);
      });
    }

    const collectionPath = `companies/${companyId}/projects/${project.id}/activityFeed`;

    const postQuery = searchQuery.trim()
      ? query(collection(db, collectionPath), orderBy('createdAt', 'desc'))
      : query(collection(db, collectionPath), orderBy('createdAt', 'desc'), limit(POSTS_PAGE_SIZE));

    return new Observable<IPost[]>((subscriber) => {
      subscriber.next(initial);

      const unsubscribe = onSnapshot(postQuery, (snapshot) => {
        const postData: IPost[] = snapshot.docs.map((doc) =>
          IPost.fromDocumentData({
            ...doc.data(),
            id: doc.id,
            parentPath: collectionPath,
            companyId: companyId,
            projectId: project.id.toString(),
          })
        );
        const sortedPosts = postData.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        subscriber.next(sortedPosts);
      });

      return () => {
        unsubscribe();
      };
    });
  },
  { initialValue: [] }
);

export const activityFeedAllLoadedPostsAtom = atom((get) => {
  const realtimePosts = get(activityFeedPostsAtom);
  const additionalPosts = get(activityFeedAdditionalPostsAtom);
  const searchQuery = get(setActivityFeedSearchAtom);

  if (searchQuery.trim()) {
    return realtimePosts;
  }

  const postsMap = new Map<string, IPost>();
  [...additionalPosts, ...realtimePosts].forEach((post) => {
    if (post.id) postsMap.set(post.id, post);
  });

  return Array.from(postsMap.values()).sort((a, b) => {
    const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
    const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
    return dateB.getTime() - dateA.getTime();
  });
});

export const filteredActivityFeedPostsAtomFamily = atomFamily((searchQuery: string) =>
  atom((get) => {
    const posts = get(activityFeedPostsAtom);

    if (!searchQuery.trim()) {
      return posts;
    }

    return posts.filter((post: IPost) => {
      if (postMatchesSearch(post, searchQuery)) {
        return true;
      }

      const comments = get(
        filteredPostCommentsAtom({ parentPath: `${post.parentPath}/${post.id}`, searchQuery })
      );
      return comments.some((comment) => commentMatchesSearch(comment, searchQuery));
    });
  })
);

export const filteredActivityFeedPostsAtom = atom((get) => {
  const posts = get(activityFeedAllLoadedPostsAtom);
  const searchQuery = get(setActivityFeedSearchAtom);
  const filter = get(activityFeedFilterAtom);

  return posts.filter((post) => {
    if (post.status === IPostStatus.DRAFT) return false;
    if (!postMatchesFilter(post, filter)) return false;

    if (searchQuery.trim()) {
      if (postMatchesSearch(post, searchQuery)) return true;

      const comments = get(
        filteredPostCommentsAtom({ parentPath: `${post.parentPath}/${post.id}`, searchQuery })
      );
      return comments.some((comment) => commentMatchesSearch(comment, searchQuery));
    }

    return true;
  });
});

export const activityFeedAppliableFiltersAtom = atom((get) => {
  const posts = get(activityFeedAllLoadedPostsAtom);

  const phases = new Set<number>();
  const phaseItems = new Set<number>();
  const profileIds = new Set<number>();

  posts.forEach((post) => {
    post.phaseIds?.forEach((id) => phases.add(id));
    post.phaseItemIds?.forEach((id) => phaseItems.add(id));
    const userId = Number(post.createdBy?.userId);
    if (userId) profileIds.add(userId);
  });

  return {
    phases: Array.from(phases),
    phaseItems: Array.from(phaseItems),
    profileIds: Array.from(profileIds),
  };
});

export const activityFeedPostsSyncAtom = atom(null, async (get, set) => {
  const loaded = get(loadable(activityFeedPostsAtom));
  if (loaded.state === 'hasData') {
    set(activityFeedPostsCacheAtom, loaded.data);
  }
});

export const loadMoreActivityFeedPostsAtom = atom(null, async (get, set) => {
  const project = get(selectedProjectAtom);
  const companyId = get(selectedCompanyIdAtom);
  const isLoadingMore = get(loadingMoreBase);
  const hasMore = get(hasMoreBase);
  const searchQuery = get(setActivityFeedSearchAtom);
  const allPosts = get(activityFeedAllLoadedPostsAtom);

  if (!project?.id || isLoadingMore || !hasMore || searchQuery.trim()) {
    return;
  }

  set(loadingMoreBase, true);

  try {
    const collectionPath = `companies/${companyId}/projects/${project.id}/activityFeed`;
    const lastPost = allPosts[allPosts.length - 1];

    if (!lastPost) {
      set(hasMoreBase, false);
      set(loadingMoreBase, false);
      return;
    }

    const lastCreatedAt = lastPost.createdAt instanceof Date
      ? Timestamp.fromDate(lastPost.createdAt)
      : Timestamp.fromDate(new Date(lastPost.createdAt));

    const nextPageQuery = query(
      collection(db, collectionPath),
      orderBy('createdAt', 'desc'),
      startAfter(lastCreatedAt),
      limit(POSTS_PAGE_SIZE)
    );

    const snapshot = await getDocs(nextPageQuery);

    if (snapshot.empty || snapshot.docs.length < POSTS_PAGE_SIZE) {
      set(hasMoreBase, false);
    }

    if (!snapshot.empty) {
      const newPosts: IPost[] = snapshot.docs.map((doc) =>
        IPost.fromDocumentData({
          ...doc.data(),
          id: doc.id,
          parentPath: collectionPath,
          companyId: companyId,
          projectId: project.id.toString(),
        })
      );

      const existingIds = new Set(allPosts.map((p) => p.id));
      const uniqueNewPosts = newPosts.filter((p) => !existingIds.has(p.id));
      set(additionalPostsBase, (prev) => [...prev, ...uniqueNewPosts]);
    }
  } catch {
  } finally {
    set(loadingMoreBase, false);
  }
});

export const resetActivityFeedPaginationAtom = atom(null, (_get, set) => {
  set(hasMoreBase, true);
  set(loadingMoreBase, false);
  set(additionalPostsBase, []);
});

const MEDIA_TAB_BATCH_SIZE = 500;

const mediaTabAllPostsBase = atom<IPost[]>([]);
export const mediaTabAllPostsAtom = atom((get) => get(mediaTabAllPostsBase));
export const mediaTabLoadingAtom = atom(false);

let mediaTabRequestId = 0;

export const loadMediaTabPostsAtom = atom(null, async (get, set) => {
  const project = get(selectedProjectAtom);
  const companyId = get(selectedCompanyIdAtom);

  if (!project?.id) return;

  const thisRequestId = ++mediaTabRequestId;
  set(mediaTabLoadingAtom, true);

  try {
    const collectionPath = `companies/${companyId}/projects/${project.id}/activityFeed`;
    const allPosts: IPost[] = [];
    let lastDoc: any = null;
    let isFirstBatch = true;

    while (true) {
      // Abort if a newer request was triggered (project changed)
      if (thisRequestId !== mediaTabRequestId) return;

      const batchQuery = isFirstBatch
        ? query(collection(db, collectionPath), orderBy('createdAt', 'desc'), limit(MEDIA_TAB_BATCH_SIZE))
        : query(collection(db, collectionPath), orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(MEDIA_TAB_BATCH_SIZE));

      const snapshot = await getDocs(batchQuery);

      if (snapshot.empty) break;

      const posts = snapshot.docs.map((docSnap) =>
        IPost.fromDocumentData({
          ...docSnap.data(),
          id: docSnap.id,
          parentPath: collectionPath,
          companyId: companyId,
          projectId: project.id.toString(),
        })
      );

      allPosts.push(...posts);
      lastDoc = snapshot.docs[snapshot.docs.length - 1];
      isFirstBatch = false;

      if (snapshot.docs.length < MEDIA_TAB_BATCH_SIZE) break;
    }

    if (thisRequestId === mediaTabRequestId) {
      set(mediaTabAllPostsBase, allPosts);
    }
  } catch (error) {
    console.error('Error loading posts for media tab:', error);
  } finally {
    if (thisRequestId === mediaTabRequestId) {
      set(mediaTabLoadingAtom, false);
    }
  }
});

export const resetMediaTabPostsAtom = atom(null, (_get, set) => {
  set(mediaTabAllPostsBase, []);
  set(mediaTabLoadingAtom, false);
});

export const addActivityFeedPostAtom = atom(
  null,
  async (
    get,
    set,
    params: {
      newPost: Partial<IPost>;
      user: { displayName: string; avatarUrl: string; userId: string, role: string };
    }
  ) => {
    // Ensure Firebase authentication is valid
    let firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      const refreshSuccess = await refreshFirebaseToken();
      if (!refreshSuccess) {
        throw new Error('Failed to authenticate with Firebase');
      }
      firebaseUser = auth.currentUser;
    }

    const { newPost, user } = params;
    const project = get(selectedProjectAtom)!;
    const companyId = get(selectedCompanyIdAtom);

    // Use provided ID if available (from frontend-generated postId)
    // Otherwise generate a new document reference to get an ID
    let postDocRef;
    if (newPost.id) {
      postDocRef = doc(
        db,
        `companies/${companyId}/projects/${project.id}/activityFeed/${newPost.id}`
      );
    } else {
      postDocRef = doc(
        collection(db, `companies/${companyId}/projects/${project.id}/activityFeed`)
      );
    }

    const draftPost: Partial<IPost> = {
      id: postDocRef.id,
      projectId: project.id.toString(),
      companyId,
      isDeleted: false,
      isEdited: false,
      replyCount: 0,
      createdAt: new Date(),
      message: newPost.message || '',
      mentions: newPost.mentions || [],
      hashtags: newPost.hashtags || [],
      tags: newPost.tags || [],
      likes: [],
      media: newPost.media || [],
      status: IPostStatus.DRAFT,
      postType: IPostType.USER_POST,
      createdBy: {
        userId: user?.userId || '',
        displayName: user?.displayName || '',
        avatarUrl: user?.avatarUrl || '',
        role: user?.role || '',
      },
      postVisibility: newPost.postVisibility,
      audienceGroups: newPost.audienceGroups,
      audienceRoles: newPost.audienceRoles,
      visibleProfileIds: newPost.visibleProfileIds,
      visibility: newPost.visibility,
      phaseIds: newPost.phaseIds || [],
      phaseItemIds: newPost.phaseItemIds || [],
    };

    // Add to local drafts immediately
    set(localActivityFeedDraftPostsAtom, (drafts) => [draftPost, ...drafts]);

    try {
      const finalPost = {
        ...draftPost,
        status: IPostStatus.ACTIVE,
        id: postDocRef.id,
        createdAt: serverTimestamp() as any,
        companyId: Number(companyId),
        projectId: project.id.toString(),
        isDeleted: false,
        isEdited: false,
        // isLocalDraft: false,
      } as unknown as IPost;

      if (finalPost.message !== '<p></p>\n' || finalPost.media.length > 0) {
        await setDoc(postDocRef, finalPost);
        // Remove draft + rely on real-time Firestore update to insert final post
        set(localActivityFeedDraftPostsAtom, (drafts) =>
          drafts.filter((d) => d.id !== postDocRef.id)
        );
      }
    } catch (error) {
      console.error('Error creating post:', error);
      set(localActivityFeedDraftPostsAtom, (drafts) =>
        drafts.filter((d) => d.id !== postDocRef.id)
      );
      throw error;
    }
  }
);

export const updateActivityFeedPostAtom = atom(
  null,
  async (get, set, post: Partial<IPost>) => {
    let firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      const refreshSuccess = await refreshFirebaseToken();
      if (!refreshSuccess) {
        throw new Error('Failed to authenticate with Firebase');
      }
      firebaseUser = auth.currentUser;
    }

    const project = get(selectedProjectAtom)!;
    const companyId = get(selectedCompanyIdAtom);

    const docPath = `companies/${companyId}/projects/${project.id}/activityFeed/${post.id}`;
    const docRef = doc(db, docPath);

    const updateData = {
      ...post,
      updatedAt: serverTimestamp(),
      updatedBy: {
        userId: firebaseUser.uid,
        displayName: firebaseUser.displayName || '',
        avatarUrl: firebaseUser.photoURL || '',
      },
      ...(post.status === IPostStatus.ACTIVE && post.savedAsDraft === false && {
        createdAt: serverTimestamp(),
      }),
    };

    await setDoc(docRef, updateData, { merge: true });
  }
);

export const deleteActivityFeedPostAtom = atom(null, async (get, set, id: string) => {
  const project = get(selectedProjectAtom)!;
  const companyId = get(selectedCompanyIdAtom);
  if (!project?.id || !companyId) return;

  // Step 1: Delete Firestore post doc — UI updates instantly via listener
  const { deleteAtom } = createCrudAtoms<IPost>(
    `companies/${companyId}/projects/${project.id}/activityFeed`
  );
  await set(deleteAtom, id);

  // Step 2: Background — delete comments subcollection + FC records (fire-and-forget)
  void (async () => {
    try {
      const commentsPath = `companies/${companyId}/projects/${project.id}/activityFeed/${id}/comments`;
      await Promise.allSettled([
        (async () => {
          const commentsSnap = await getDocs(collection(db, commentsPath));
          await Promise.all(commentsSnap.docs.map((commentDoc) => deleteDoc(commentDoc.ref)));
        })(),
        deleteMediaEntriesByPost(project.id, id),
      ]);
    } catch (err) {
      console.error('[ActivityFeed] Background delete cleanup failed:', err);
    }
  })();
});

export const lastViewedActivityFeedAtom = atomWithStorage<Record<string, number>>(
  'lastViewedActivityFeed',
  {}
);

export const getUnviewedActivityFeedCount = (
  posts: IPost[],
  projectId: number | string,
  userId: number | string,
  lastViewedMap: Record<string, number>
): number => {
  if (!projectId || !posts.length || !userId) {
    return 0;
  }

  const key = `${userId}_${projectId}`;
  const lastViewedTime = lastViewedMap[key] || 0;

  return posts.filter((post) => {
    const postTime = post.createdAt instanceof Date
      ? post.createdAt.getTime()
      : new Date(post.createdAt).getTime();
    return postTime > lastViewedTime && post.createdBy?.userId !== String(userId);
  }).length;
};

export const markActivityFeedViewedAtom = atom(
  null,
  (get, set, userId: number | string) => {
    const project = get(selectedProjectAtom);
    if (!project?.id || !userId) return;

    const key = `${userId}_${project.id}`;
    set(lastViewedActivityFeedAtom, (prev) => ({
      ...prev,
      [key]: Date.now(),
    }));
  }
);

const isImageOrVideo = (url: string, mimeType?: string): { isMedia: boolean; type: 'image' | 'video' } => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.m4v'];

  const lowerUrl = url.toLowerCase();

  if (mimeType) {
    if (mimeType.startsWith('image/')) return { isMedia: true, type: 'image' };
    if (mimeType.startsWith('video/')) return { isMedia: true, type: 'video' };
  }

  for (const ext of imageExtensions) {
    if (lowerUrl.includes(ext)) return { isMedia: true, type: 'image' };
  }
  for (const ext of videoExtensions) {
    if (lowerUrl.includes(ext)) return { isMedia: true, type: 'video' };
  }

  return { isMedia: false, type: 'image' };
};

export const activityFeedMediaAtom = atom<TActivityFeedMediaMapped>((get) => {
  const mediaTabPosts = get(mediaTabAllPostsAtom);
  const realtimePosts = get(activityFeedPostsAtom);

  const postsMap = new Map<string, IPost>();
  mediaTabPosts.forEach((p) => postsMap.set(p.id, p));
  realtimePosts.forEach((p) => postsMap.set(p.id, p));
  const posts = Array.from(postsMap.values());

  const realtimePostIds = new Set(realtimePosts.map((p) => p.id));

  const mediaItems: TActivityFeedMediaWithIndex[] = [];

  let globalIndex = 0;

  posts.forEach((post) => {
    const videoExtRegex = /\.(mp4|mov|avi|mkv|webm|m4v)$/i;
    const imageExtRegex = /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic|heif|tiff|tif|avif)$/i;
    const postMediaFiles = post.media?.filter((m) => {
      if (m.type === 'image' || m.type === 'video') return true;
      if (m.type === 'file') {
        const name = m.name || '';
        return videoExtRegex.test(name) || imageExtRegex.test(name);
      }
      return false;
    }) || [];

    postMediaFiles.forEach((mediaItem) => {
      const nameParts = (post.createdBy?.displayName || '').split(' ');
      mediaItems.push({
        id: `post_${post.id}_${globalIndex}`,
        url: mediaItem.url,
        name: mediaItem.name || '',
        mimeType: (mediaItem.type === 'video' || videoExtRegex.test(mediaItem.name || '')) ? 'video/mp4' : 'image/jpeg',
        description: mediaItem.description || null,
        location: mediaItem.location || null,
        timestamp: mediaItem.timestamp || null,
        options: (() => {
          const thumbUrl = mediaItem.transcoded?.thumbnail || mediaItem.options?.previewUrl;
          return thumbUrl
            ? { soundLevel: mediaItem.options?.soundLevel || [], duration: mediaItem.options?.duration || '', previewUrl: thumbUrl }
            : null;
        })(),
        status: UploadStatusEnum.COMPLETED,
        createdAt: post.createdAt instanceof Date ? post.createdAt : new Date(post.createdAt),
        profile: {
          id: parseInt(post.createdBy?.userId || '0', 10),
          userId: parseInt(post.createdBy?.userId || '0', 10),
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          role: post.createdBy?.role || '',
          avatar: {
            id: '',
            url: post.createdBy?.avatarUrl || '',
            name: 'avatar',
          },
        },
        index: globalIndex,
      });
      globalIndex++;
    });

    // Only include comment media from posts with active realtime listeners
    if (!realtimePostIds.has(post.id)) return;

    const parentPath = `${post.parentPath}/${post.id}`;
    const comments = get(postCommentsAtom(parentPath)) || [];

    comments.forEach((comment) => {
      const uploads = comment.uploads || [];

      uploads.forEach((upload: any) => {
        const isObject = typeof upload === 'object' && upload !== null;
        const url: string = isObject ? upload.url : upload;
        const name: string = isObject && upload.name ? upload.name : url.split('/').pop() || '';

        const { isMedia, type } = isImageOrVideo(url);

        if (isMedia) {
          const nameParts = (comment.createdBy?.displayName || '').split(' ');
          mediaItems.push({
            id: `comment_${comment.id}_${globalIndex}`,
            url: url,
            name: name,
            mimeType: type === 'video' ? 'video/mp4' : 'image/jpeg',
            description: null,
            location: null,
            timestamp: null,
            options: null,
            status: UploadStatusEnum.COMPLETED,
            createdAt: comment.createdAt instanceof Date ? comment.createdAt : new Date(comment.createdAt),
            profile: {
              id: parseInt(comment.createdBy?.userId || '0', 10),
              userId: parseInt(comment.createdBy?.userId || '0', 10),
              firstName: nameParts[0] || '',
              lastName: nameParts.slice(1).join(' ') || '',
              role: comment.createdBy?.role || '',
              avatar: {
                id: '',
                url: comment.createdBy?.avatarUrl || '',
                name: 'avatar',
              },
            },
            index: globalIndex,
          });
          globalIndex++;
        }
      });
    });
  });

  mediaItems.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  mediaItems.forEach((item, idx) => {
    item.index = idx;
  });

  const groupedByDate: Record<string, TActivityFeedMediaWithIndex[]> = {};

  mediaItems.forEach((item) => {
    const dateKey = item.createdAt.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    if (!groupedByDate[dateKey]) {
      groupedByDate[dateKey] = [];
    }
    groupedByDate[dateKey].push(item);
  });

  const items: [string, TActivityFeedMediaWithIndex[]][] = Object.entries(groupedByDate);

  return {
    items,
    meta: {
      totalItems: mediaItems.length,
    },
  };
});

export const toggleActivityFeedPostLikeAtom = atom(
  null,
  async (
    get,
    set,
    payload: {
      postId: string;
      userId: string;
    }
  ) => {
    // Check Firebase auth state first
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error('No Firebase user authenticated');
      throw new Error('User must be authenticated to like posts');
    }

    const { postId, userId } = payload;

    if (!postId) {
      console.error('Post ID missing:', { postId });
      throw new Error('Post ID is required');
    }

    // Check what's in the cache
    const allPosts = get(activityFeedPostsAtom);

    const post = allPosts.find((p) => p.id === postId);

    if (!post) {
      console.error('Post not found with ID:', postId);
      console.error('Error getting post from Firestore:', postId);
      return;
    }

    const hasLiked = post.likes?.includes(userId) ?? false;
    const newlikes = hasLiked
      ? post.likes?.filter((uid) => uid !== userId)
      : [...(post.likes || []), userId];

    // Use direct Firestore update instead of createCrudAtoms
    try {
      // Update Firestore directly - let the listener handle the UI update (same as addPost)
      await set(updateActivityFeedPostAtom, {
        ...post,
        likes: newlikes,
      });
    } catch (error) {
      console.error('Error updating Firestore:', error);
      throw error;
    }
  }
);
